/**
 * Spec №11 (D-M4) — the client-side workbench registry. Workbenches are named
 * React components; a sitemap node may target `workbench:<name>`, and the App
 * Shell generates their routes from this registry — ending the sitemap / route /
 * flag triple-hardcoding the old `sitemap.ts` header called out.
 *
 * The eight ledger pages are registered here. `chrome:"shell"` routes mount
 * inside the App Shell (nav + stage + work rail); `chrome:"bare"` mount with
 * their own AuthGuard and no shell chrome (the print-preview surface, 10.3.1 §1).
 * Paths are exactly today's, so every ledger workbench stays reachable unchanged.
 */
import type { ComponentType } from "react";
import { TrustPage } from "./TrustPage";
import { LedgerCockpitPage } from "./LedgerCockpitPage";
import { AccountViewPage } from "./AccountViewPage";
import { ReconciliationPage } from "./ReconciliationPage";
import { PeriodClosePage } from "./PeriodClosePage";
import { PeriodHistoryPage } from "./PeriodHistoryPage";
import { ComposerPage } from "./ComposerPage";
import { LedgerPrintPage } from "./LedgerPrintPage";
import { DayViewPage } from "./DayViewPage";
import { WipBillingWorksheetPage } from "./WipBillingWorksheetPage";
import { InvoicesListPage } from "./InvoicesPage";
import { VendorInvoicesPage } from "./VendorInvoicesPage";
import { RegistersPage } from "./RegistersPage";
import { ClientsPage } from "./ClientsPage";
import { FirmSettingsPage } from "./FirmSettingsPage";

export interface WorkbenchDef {
  /** Stable name a sitemap `workbench:` target resolves to. */
  name: string;
  /** Route path. Relative to `/app` for shell workbenches; absolute for bare. */
  path: string;
  component: ComponentType;
  /** `shell` = inside AppShellRoot chrome; `bare` = own AuthGuard, no chrome. */
  chrome: "shell" | "bare";
}

export const WORKBENCH_REGISTRY: WorkbenchDef[] = [
  { name: "trust", path: "trust", component: TrustPage, chrome: "shell" },
  { name: "ledger-cockpit", path: "trust/:ledgerId", component: LedgerCockpitPage, chrome: "shell" },
  { name: "account-view", path: "trust/:ledgerId/accounts/:accountId", component: AccountViewPage, chrome: "shell" },
  { name: "reconciliation", path: "trust/:ledgerId/reconciliation", component: ReconciliationPage, chrome: "shell" },
  { name: "period-close", path: "trust/:ledgerId/period-close", component: PeriodClosePage, chrome: "shell" },
  { name: "period-history", path: "trust/:ledgerId/period-history", component: PeriodHistoryPage, chrome: "shell" },
  { name: "composer", path: "trust/:ledgerId/compose", component: ComposerPage, chrome: "shell" },
  { name: "ledger-print", path: "app/print/:ledgerId/:report", component: LedgerPrintPage, chrome: "bare" },
  // №29 (S2) — RETIRED: `place/matter/:id` (№13 Matter Cockpit),
  // `place/fee-arrangement/:id` and `place/invoice/:id` (№13.1). They were routes
  // with the entity type baked into the PATH, which tied with
  // `/app/place/:entity/chat` in the router's ranking and so blocked the Place-list
  // conversation (§S1.6). Which component draws a record is now a REGISTRY read on
  // the one `place/:entity/:id` route — see `record-renderers.ts`. The URLs, the
  // pages and the matrix row are all unchanged; only the registration moved.
  // Spec №12 Stage C — Time & billing workbenches.
  { name: "time-day", path: "time/day", component: DayViewPage, chrome: "shell" },
  { name: "wip-worksheet", path: "billing/wip", component: WipBillingWorksheetPage, chrome: "shell" },
  { name: "invoices", path: "billing/invoices", component: InvoicesListPage, chrome: "shell" },
  { name: "vendor-invoices", path: "billing/vendor-invoices", component: VendorInvoicesPage, chrome: "shell" },
  { name: "registers", path: "registers", component: RegistersPage, chrome: "shell" },
  // №13.2 §2.2 — the Clients lens (contacts holding the client role).
  { name: "clients", path: "clients", component: ClientsPage, chrome: "shell" },
  { name: "firm-settings", path: "settings/firm", component: FirmSettingsPage, chrome: "shell" },
];

export function findWorkbench(name: string): WorkbenchDef | undefined {
  return WORKBENCH_REGISTRY.find((w) => w.name === name);
}

/** Resolve a `workbench:<name>` nav target to a navigable route, if the
 *  workbench has a param-free landing path. Paramful workbenches (drill-downs)
 *  aren't nav destinations, so they resolve to undefined. */
export function workbenchNavRoute(name: string): string | undefined {
  const wb = findWorkbench(name);
  if (!wb || wb.path.includes(":")) return undefined;
  return wb.chrome === "shell" ? `/app/${wb.path}` : `/${wb.path}`;
}
